// src/components/MeetingCard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import './MeetingCard.css';
import { useVideoPopup } from './VideoPopupContext';
import Scorecard from './Scorecard';

function MeetingCard({
  id,
  agendaItems,
  date,
  title,
  topics,
  content,
  isUpcoming,
  meetingType,
  initialExpanded = false,
  youtubeLink,
  part,
  isWidget = false,
  onExpand,
  recordingNotice = null
}) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [linkedVotes, setLinkedVotes] = useState({});
  const { showPopup } = useVideoPopup();
  const location = useLocation();
  const contentRef = useRef(null);

  // --- SAFE NOTIFICATION ---
  useEffect(() => {
    if (expanded && onExpand) {
        onExpand(agendaItems, youtubeLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]); 

  // --- 1. Fetch Vote Links ---
  useEffect(() => {
    if (!id || !expanded) return;
    async function fetchVoteLinks() {
      const { data } = await supabase
        .from('meeting_items')
        .select('item_index, voting_id')
        .eq('meeting_id', id)
        .not('voting_id', 'is', null);

      if (data) {
        const links = {};
        data.forEach(row => {
          if (row.item_index !== null) {
            links[row.item_index - 1] = row.voting_id;
          }
        });
        setLinkedVotes(links);
      }
    }
    fetchVoteLinks();
  }, [id, expanded]);

  // --- 2. Widget Mode ---
  useEffect(() => {
    if (isWidget && expanded && contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        if (link.classList.contains('timestamp-link')) return;
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [isWidget, expanded, agendaItems, content]);

  // --- Helpers ---
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  const computedTitle =
    meetingType === 'cboc'
      ? `Citizens' Bond Oversight Committee ${title}`
      : meetingType === 'study session'
        ? `Study Session ${title}`
        : (meetingType === 'special' || meetingType === 'upcoming')
          ? `${capitalize(meetingType)} ${title}`
          : title;

  const formattedTopics = Array.isArray(topics) ? topics.join(', ') : topics;

  const safeDate = date || 'unknown';
  const baseId = `meeting-${safeDate.replace(/\//g, '-')}`;
  const meetingId = !isUpcoming
    ? (part !== null && part !== undefined ? `${baseId}-p${part}` : baseId)
    : undefined;

  // --- Auto-Expand ---
  useEffect(() => {
    const hash = location.hash.substring(1);
    if (!isUpcoming && meetingId && hash.startsWith(meetingId)) {
      setExpanded(true);
    }
  }, [location.hash, meetingId, isUpcoming]);

  // --- Auto-Scroll ---
  useEffect(() => {
    if (expanded && location.hash) {
      const hash = location.hash.substring(1);
      if (hash.startsWith(meetingId)) {
        setTimeout(() => {
          const targetElement = document.getElementById(hash);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            targetElement.classList.add('highlight-flash');
            setTimeout(() => targetElement.classList.remove('highlight-flash'), 2000);
          }
        }, 300);
      }
    }
  }, [expanded, location.hash, meetingId]);

  // --- Timestamp Logic ---
  const processContentWithTimestamps = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    if (!youtubeLink) return text;
    const timestampRegex = /\b(\d{1,2}:\d{2}:\d{2}|\d{1,2}:\d{2})\b/g;
    return text.replace(timestampRegex, (match) => {
      return `<span class="timestamp-link" data-timestamp="${match}">${match}</span>`;
    });
  };

  const handleTimestampHover = useCallback((event) => {
    const timestamp = event.target.getAttribute('data-timestamp');
    if (timestamp && youtubeLink) showPopup(timestamp, youtubeLink, event.target);
  }, [showPopup, youtubeLink]);

  // Delegated click handler: reliably fires for every .timestamp-link inside
  // the card, including dynamically-rendered spans from dangerouslySetInnerHTML.
  const handleContentClick = useCallback((event) => {
    const target = event.target.closest && event.target.closest('.timestamp-link');
    if (!target) return;
    const timestamp = target.getAttribute('data-timestamp');
    if (timestamp && youtubeLink) showPopup(timestamp, youtubeLink, target);
  }, [showPopup, youtubeLink]);

  useEffect(() => {
    if (!expanded || !youtubeLink || !meetingId) return;
    setTimeout(() => {
      const cardElement = document.getElementById(meetingId);
      if (!cardElement) return;
      const timestampElements = cardElement.querySelectorAll('.timestamp-link');
      timestampElements.forEach((el) => {
        el.addEventListener('mouseenter', handleTimestampHover);
        el.addEventListener('click', handleTimestampHover);
      });
      return () => {
        timestampElements.forEach((el) => {
          el.removeEventListener('mouseenter', handleTimestampHover);
          el.removeEventListener('click', handleTimestampHover);
        });
      };
    }, 200);
  }, [expanded, meetingId, youtubeLink, handleTimestampHover, agendaItems]);

  // --- RENDER LOGIC ---
  const renderStructuredItems = () => {
    if (!agendaItems || !Array.isArray(agendaItems)) return null;

    return agendaItems.map((item, index) => {
      const itemId = `${meetingId}-item-${index + 1}`;
      const isPublicComment = item.title && item.title.includes("Public Comment");

      // 1. Unified Timeline Box (Start -> Motion -> Vote -> End)
      const renderTimelineBox = () => {
        const start = item.timestamp_start;
        const end = item.timestamp_end;
        const { motion, vote } = item.action_timeline || {};
        
        const hasMotion = motion?.present && motion?.timestamp;
        const hasVote = vote?.present && vote?.timestamp;
        
        // If no timestamps at all, skip
        if (!start && !end && !hasMotion && !hasVote) return null;

        return (
          <div className="timeline-box" style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '15px', 
            marginTop: '8px', 
            fontSize: '0.9rem', 
            color: '#475569', 
            background: '#f8fafc', 
            padding: '6px 12px', 
            borderRadius: '4px', 
            width: 'fit-content', 
            border: '1px solid #e2e8f0',
            alignItems: 'center'
          }}>
            {start && (
               <span>
                 <strong>Start:</strong> <span className="timestamp-link" data-timestamp={start}>{start}</span>
               </span>
            )}
            {hasMotion && (
              <span>
                <strong>Motion:</strong> <span className="timestamp-link" data-timestamp={motion.timestamp}>{motion.timestamp}</span>
              </span>
            )}
            {hasVote && (
              <span style={{ color: '#0f172a', fontWeight: '600' }}>
                <strong>Vote:</strong> <span className="timestamp-link" data-timestamp={vote.timestamp}>{vote.timestamp}</span>
              </span>
            )}
            {end && (
               <span>
                 <strong>End:</strong> <span className="timestamp-link" data-timestamp={end}>{end}</span>
               </span>
            )}
          </div>
        );
      };

      // 2. Standard Intelligence Line
      const renderStandardMetadata = () => {
        const vendors = item.entities || item.intelligence_data?.vendors_and_partners || [];
        const policies = item.policy_citations || item.intelligence_data?.policy_citations || [];
        const fiscal = item.financial_impact || item.intelligence_data?.financial_impact;
        
        const hasFiscal = fiscal && fiscal.amount && fiscal.amount !== 'None' && fiscal.amount !== 'N/A';
        const hasVendors = vendors.length > 0;
        const hasPolicies = policies.length > 0;

        if (!hasFiscal && !hasVendors && !hasPolicies) return null;

        return (
          <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#64748b', lineHeight: '1.5', borderTop: '1px dashed #e2e8f0', paddingTop: '8px' }}>
             {hasFiscal && (
                 <div style={{ display: 'inline-block', marginRight: '15px' }}>
                    <span style={{ fontWeight: '600', color: '#059669' }}>Fiscal: {fiscal.amount}</span> 
                    <span style={{ color: '#94a3b8' }}> ({fiscal.funding_source})</span>
                 </div>
             )}
             {hasVendors && (
                 <div style={{ display: 'inline-block', marginRight: '15px' }}>
                    <span style={{ fontWeight: '600' }}>Entities:</span> {vendors.join(', ')}
                 </div>
             )}
             {hasPolicies && (
                 <div style={{ display: 'inline-block' }}>
                    <span style={{ fontWeight: '600' }}>Ref:</span> {policies.join(', ')}
                 </div>
             )}
          </div>
        );
      };
      
      // 3. Budget Details Block (merged from budget_discussions by timestamp_start)
      const renderBudgetDetail = () => {
        const b = item.budget_detail;
        if (!b) return null;
        const figures = Array.isArray(b.key_figures) ? b.key_figures : [];
        const fiscalYear = b.fiscal_year;
        const typeLabel = b.type
          ? b.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : null;

        if (!figures.length && !b.next_steps && !fiscalYear && !typeLabel) return null;

        return (
          <div className="budget-details" style={{
            marginTop: '1rem',
            marginLeft: '20px',
            padding: '12px 16px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '6px',
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: (figures.length || b.next_steps) ? '10px' : 0,
            }}>
              <span style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>
                Budget & Financial Details
              </span>
              {fiscalYear && (
                <span style={{ background: '#059669', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>
                  {fiscalYear}
                </span>
              )}
              {typeLabel && (
                <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>
                  {typeLabel}
                </span>
              )}
            </div>
            {figures.length > 0 && (
              <div>
                <strong style={{ color: '#065f46', fontSize: '0.88rem' }}>Key Figures:</strong>
                <ul style={{ marginTop: '4px', marginBottom: b.next_steps ? '10px' : 0, paddingLeft: '20px', listStyleType: 'disc', color: '#0f172a', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  {figures.map((fig, i) => {
                    const figureText = typeof fig === 'string' ? fig : fig?.figure;
                    const figTs = (fig && typeof fig === 'object') ? fig.timestamp : null;
                    if (!figureText) return null;
                    return (
                      <li key={i} style={{ marginBottom: '2px' }}>
                        <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(figureText) }} />
                        {figTs && (
                          <>
                            {' '}
                            <span
                              className="timestamp-link"
                              data-timestamp={figTs}
                              style={{ marginLeft: '4px', fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}
                            >
                              [{figTs}]
                            </span>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {b.next_steps && (
              <div style={{ fontSize: '0.88rem', color: '#0f172a' }}>
                <strong style={{ color: '#065f46' }}>Next Steps:</strong>{' '}
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(b.next_steps) }} />
              </div>
            )}
          </div>
        );
      };

      // Label Logic
      const contextLabel = item.summary?.speaker_tally_summary ? "Speaker Tally: " : "Context: ";
      const contextContent = item.summary?.speaker_tally_summary || item.summary?.context || item.summary?.proposal;

      return (
        <div
          key={index}
          id={itemId}
          className="meeting-item meeting-item-target"
          style={{ marginBottom: '2.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '2rem' }}
        >
          {/* Header & Outcome Badge */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', maxWidth: '85%' }}>
              {item.title}
            </h4>
            {item.outcome && item.outcome !== 'N/A' && (
              <span style={{ 
                backgroundColor: item.outcome.includes('Pass') || item.outcome.includes('Approve') ? '#dcfce7' : '#f1f5f9',
                color: item.outcome.includes('Pass') || item.outcome.includes('Approve') ? '#166534' : '#475569',
                padding: '4px 10px', 
                borderRadius: '12px', 
                fontSize: '0.8rem', 
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                {item.outcome}
              </span>
            )}
          </div>

          {/* Unified Timeline Box */}
          {renderTimelineBox()}
          
          {/* Metadata Line */}
          {renderStandardMetadata()}

          {/* Summary Content */}
          <div style={{ paddingLeft: '20px', marginTop: '1rem', lineHeight: '1.7', color: '#1e293b' }}>
            
            {/* Context / Speaker Tally */}
            {contextContent && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: '#334155' }}>
                    {contextLabel}
                </strong>
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(contextContent) }} />
              </div>
            )}

            {/* Themes (Public Comment) */}
            {(item.summary?.themes || item.summary?.impact) && Array.isArray(item.summary?.themes || item.summary?.impact) && (item.summary?.themes || item.summary?.impact).length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: '#334155' }}>Themes:</strong>
                <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '20px', listStyleType: 'square' }}>
                  {(item.summary.themes || item.summary.impact).map((theme, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem' }}>
                      <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(theme) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Discussion Log */}
            {item.summary?.discussion_log && item.summary.discussion_log.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: '#334155' }}>Discussion Log:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '20px', listStyleType: 'disc' }}>
                  {item.summary.discussion_log.map((entry, idx) => (
                    <li key={idx} style={{ marginBottom: '0.75rem' }}>
                      <strong>{entry.role}:</strong>{' '}
                      <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(entry.point) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Speakers List (Public Comment) */}
            {isPublicComment && item.summary?.speakers && Array.isArray(item.summary.speakers) && item.summary.speakers.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: '#334155' }}>Speakers:</strong>
                <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '20px', listStyleType: 'square' }}>
                  {item.summary.speakers.map((spk, idx) => (
                    <li key={idx} style={{ marginBottom: '0.5rem' }}>
                      <strong>{spk.role || 'Speaker'}</strong>
                      {' - '}
                      <strong>Time: </strong>
                      <span className="timestamp-link" data-timestamp={spk.timestamp}>{spk.timestamp}</span>
                      {' - '}
                      <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(spk.summary || spk.topic_summary) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fallback for Old "Debate" Format */}
            {!item.summary?.discussion_log && item.summary?.debate && !isPublicComment && (
               <div style={{ marginBottom: '1rem' }}>
                 <strong>Debate:</strong>
                 {typeof item.summary.debate === 'string' ? (
                    <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.debate) }} />
                 ) : (
                    <ul style={{ marginTop: '0.5rem', paddingLeft: '20px' }}>
                      {item.summary.debate.for?.length > 0 && (
                        <li>
                          <strong>For:</strong>{' '}
                          <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.debate.for.join(' ')) }} />
                        </li>
                      )}
                      {item.summary.debate.against?.length > 0 && (
                        <li>
                          <strong>Against:</strong>{' '}
                          <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.debate.against.join(' ')) }} />
                        </li>
                      )}
                    </ul>
                 )}
               </div>
            )}

            {/* Decision / Next */}
            {item.summary?.decision && (
              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: '#334155' }}>Decision / Next: </strong>
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.decision) }} />
              </div>
            )}
          </div>

          {/* Budget Details (merged from budget_discussions) */}
          {renderBudgetDetail()}

          {/* Scorecard (Voting Viz) */}
          {linkedVotes[index] && (
            <div className="mt-4" style={{ marginTop: '1.5rem', marginLeft: '20px' }}>
              <Scorecard votingId={linkedVotes[index]} />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <div className="meeting-card" id={meetingId}>
        <div
          className="meeting-header"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
        >
          <div className="meeting-info">
            <h3 className="meeting-title">
              {computedTitle} {formatDate(date)}
              {part !== null && part !== undefined && ` - Part ${part}`}
              {isUpcoming && <span className="upcoming-badge">Upcoming</span>}
              {recordingNotice && <span className="no-recording-badge" title="The district did not make a recording publicly available">No District Recording</span>}
            </h3>
            <div className="meeting-subheading">Topics: {formattedTopics}</div>
          </div>
          <button
            className="expand-button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? 'Minimize' : 'Expand'}
          </button>
        </div>

        {expanded && (
          <div className="meeting-content mt-4" ref={contentRef} onClick={handleContentClick}>
            {recordingNotice && (
              <div className="recording-notice-banner" role="note">
                <span className="recording-notice-label">Notice — No District Recording</span>
                <span className="recording-notice-text">{recordingNotice}</span>
              </div>
            )}
            {agendaItems && agendaItems.length > 0 ? (
              renderStructuredItems()
            ) : (
              <div dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(content) }} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MeetingCard;
